<template>
	<div
		class="pronto-visual-media"
		:class="{
			gif: media.isGif,
			autoplay: autoPlaying,
			rounded,
			video: showVideo,
			'load-error': loadError,
		}"
		@mouseleave="onMouseLeave"
		@click="onMediaClick"
	>
		<img
			class="pronto-visual-image ph-no-capture"
			v-if="media.isVisual"
			:src="imageUrl"
			@error="onLoadError"
			@click="onLoadRetry"
		/>
		<video
			class="video-player ph-no-capture"
			v-if="media.isVideo && !loadError"
			:src="videoUrl"
			autoplay
			controls
			@play="playVideo"
			@error="onLoadError"
			@click="onLoadRetry"
			ref="videoElement"
		/>
		<div v-if="media.isGif" class="gif-icon">
			<GifTextIcon />
		</div>
		<div
			class="video-overlay"
			v-if="media.isVideo && !showVideo"
			@click.stop="onClickVideoOverlay"
		>
			<PlayIcon type="solid" />
			<div
				class="video-duration"
				v-if="duration && !loadError"
				v-text="duration"
			/>
		</div>
	</div>
</template>

<script setup>
import { nextTick, watch, ref, computed, inject } from 'vue'
import { GifTextIcon, PlayIcon } from '@/icons'
import { IMAGE_MAX_WIDTH } from '@/helpers/constants'
import { generateGifThumbnailFromUrl } from '@/helpers/utils'
import { useEventBus } from '@/helpers/bus'
import moment from 'moment'
import { createLogger } from '@/helpers/log'
import { useMediaModal } from '@/components/common/MediaModal'
const props = defineProps({
	media: Object,
	rounded: { type: Boolean, default: false },
	autoPlayGif: { type: Boolean, default: false },
	playVideo: { type: Boolean, default: true },
	openOnClick: { type: Boolean, default: false },
})

const modal = useMediaModal()

const log = createLogger('VisualMedia')
const autoPlaying = ref(props.media.isGif ? props.autoPlayGif : false)
const showVideo = ref(false)
const gifThumbnail = ref(props.media.thumbnailUrl || null)
const loadError = ref(null)
const MAX_SIZE = inject('maxMediaSize') || IMAGE_MAX_WIDTH

const gifStillUrl = computed(() =>
	gifThumbnail.value ? `url(${gifThumbnail.value})` : null
)
const duration = computed(() => {
	const { isVideo, duration } = props.media || {}
	if (!isVideo || !duration) return null
	const converted = moment.utc(duration * 1000)
	return converted.format(duration >= 3600 ? 'HH:mm:ss' : 'mm:ss')
})

const imageUrl = computed(() => {
	const { media } = props
	if (!media?.isVisual || loadError.value) return null
	return media.isVideo ? media.thumbnailUrl : media.mediaUrl
})

const videoUrl = computed(() => {
	const { media } = props
	if (!media?.isVideo || !showVideo.value || loadError.value) return null
	return media.mediaUrl
})

// a bunch of annoying math to properly handle all various aspect ratios
const { width, height } = props.media
const aspectRatio = width / height
const largest = Math.max(width, height)
const isPortrait = largest === height
let maxWidth = Math.min(width, MAX_SIZE)
let maxHeight = Math.min(height, MAX_SIZE)
if (largest > MAX_SIZE) {
	if (isPortrait) {
		maxWidth = width * (MAX_SIZE / height)
	} else {
		maxHeight = height * (MAX_SIZE / width)
	}
}

const videoElement = ref(null)
const bus = useEventBus('video-playing', (id) => {
	if (props.media.id === id) return
	videoElement.value?.pause?.()
})
const playVideo = () => {
	bus.$emit('video-playing', props.media.id)
}

function onMouseLeave() {
	if (autoPlaying.value) {
		autoPlaying.value = false
	}
}

function onMediaClick() {
	if (!props.openOnClick) return
	if (props.media.isVideo && !props.playVideo) return
	if (props.media.isVideo) {
		modal.video(props.media.mediaUrl, props.media.name)
	} else {
		modal.image(props.media.mediaUrl, props.media.name)
	}
}

function onClickVideoOverlay() {
	if (!props.playVideo) return
	showVideo.value = true
}

function onLoadError(e) {
	log('media failed to load', e.srcElement?.src, e)
	loadError.value = true
}

function onLoadRetry(e) {
	if (loadError.value) {
		log(
			'refetching failed media...',
			props.media?.isVideo ? videoUrl.value : imageUrl.value
		)
		e.stopPropagation()
		loadError.value = null
	}
}

function grabFrameOfGif() {
	if (props.media.isGif && !gifThumbnail.value) {
		generateGifThumbnailFromUrl(props.media.mediaUrl)
			.then((blobUrl) => {
				gifThumbnail.value = blobUrl
			})
			.catch((e) => console.error(e))
	}
}

nextTick(() => {
	grabFrameOfGif()
	// thumbnail generation can be resource intensive, so we want to offload that until
	// after the remainder of the grid has rendered
	watch(props.media, () => {
		// if gifs get added, we need to generate a "paused" thumbnail from the file
		grabFrameOfGif()
	})
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-visual-media {
	@include flex;
	justify-content: center;
	position: relative;
	width: calc(v-bind(width) * 1px);
	height: calc(v-bind(height) * 1px);
	max-width: calc(v-bind(maxWidth) * 1px);
	max-height: calc(v-bind(maxHeight) * 1px);
	aspect-ratio: v-bind(aspectRatio);
	background-color: var(--neutral-200);

	&.rounded {
		@include radius;
		overflow: hidden;
	}
	&.gif {
		background-image: v-bind(gifStillUrl);
		background-position: center;
		background-repeat: no-repeat;
		background-size: cover;
		&:hover,
		&.autoplay {
			.pronto-visual-image {
				opacity: 1;
			}
			.gif-icon {
				opacity: 0;
			}
		}
		.pronto-visual-image {
			opacity: 0;
		}
	}
	&.video {
		.video-player {
			z-index: 2;
			opacity: 1 !important;
		}
	}
	&.load-error {
		background-image: url('/images/common/broken-image.svg');
		background-size: 20%;
		background-position: center;
		background-repeat: no-repeat;
		.pronto-visual-image {
			opacity: 0;
		}
	}

	.pronto-visual-image {
		width: calc(v-bind(width) * 1px);
		height: calc(v-bind(height) * 1px);
		max-width: 100%;
		max-height: 100%;
		aspect-ratio: v-bind(aspectRatio);
		z-index: 1;
	}

	.video-player {
		@include absolute-cover(0);
		width: 100%;
		height: 100%;
		opacity: 0;
	}

	.video-overlay {
		position: relative;
		@include absolute-cover(2);
		@include flex;
		justify-content: center;
		background-color: rgba(0, 0, 0, 0.4);
		z-index: 1;
		cursor: pointer;
		.pronto-icon {
			font-size: 32px;
			color: white;
		}
		.video-duration {
			text-shadow: 0 0 4px rgba(44, 45, 48, 0.8);
			@include fontFamily(900);
			color: white;
			font-size: 16px;
			position: absolute;
			bottom: 8px;
			right: 8px;
			line-height: 1;
			z-index: 2;
		}
	}

	.gif-icon {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 3;
		@include flex;
		justify-content: center;
		background: rgba(0, 0, 0, 0.65);
		width: 36px;
		height: 29px;
		opacity: 1;
		transition: opacity ease-in-out 150ms;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pronto-visual-media {
		background-color: var(--neutral-300);
	}
}
</style>
